import { ChevronUp } from "lucide-react";
import { forwardRef, type ReactNode } from "react";
import { useSelector } from "react-redux";

import { useContextLabel, type AgentState, type TargetKey } from "@/agent";
import { AssistantMark } from "@/agent/theme";
import { Badge } from "@/components/ui/badge";

import { selectHasPendingChoice, selectLipActivity } from "./selectors";

type State = { agent: AgentState };

const ACTIVITY_LABEL = { thinking: "Thinking…", writing: "Writing…" } as const;

/** What the bar says is happening: a choice waits (shown while closed), or the run's activity. */
function useLipStatus(targetKey: TargetKey, open: boolean) {
  const activity = useSelector((state: State) => selectLipActivity(state, targetKey));
  const waiting = useSelector((state: State) => selectHasPendingChoice(state, targetKey));
  // Open, the thread shows the choice itself.
  if (waiting && !open) return { waiting: true, text: "Needs you" } as const;
  return activity ? ({ waiting: false, text: ACTIVITY_LABEL[activity] } as const) : null;
}

function LipStatus({ status }: { status: ReturnType<typeof useLipStatus> }) {
  if (!status) return null;
  if (status.waiting) {
    return (
      <Badge className="border-transparent bg-warning-soft text-warning shadow-none">
        {status.text}
      </Badge>
    );
  }
  return (
    <Badge className="gap-1.5 border-transparent bg-accent text-accent-foreground shadow-none">
      <span className="h-[7px] w-[7px] rounded-full bg-primary motion-safe:animate-ring-pulse" />
      {status.text}
    </Badge>
  );
}

interface LipBarProps {
  targetKey: TargetKey;
  open: boolean;
  unseen: number;
  onToggle: () => void;
  /** The id of the lip's body, which the toggle opens. */
  controls: string;
  /** Controls after the toggle, on the bar's right edge. */
  children?: ReactNode;
}

/** The lip's always-visible row: the mark, the title, the context, and what is happening. */
export const LipBar = forwardRef<HTMLButtonElement, LipBarProps>(function LipBar(
  { targetKey, open, unseen, controls, onToggle, children },
  ref
) {
  const threadId = useSelector((state: State) => state.agent.targets[targetKey]?.threadId ?? null);
  const label = useContextLabel(threadId);
  const status = useLipStatus(targetKey, open);
  return (
    <div className="flex h-[var(--assistant-lip-peek)] shrink-0 items-center pr-2.5">
      <button
        ref={ref}
        type="button"
        aria-expanded={open}
        aria-controls={controls}
        data-lip-bar=""
        onClick={onToggle}
        // A rounded ring: every corner, and the lip's own radius where it meets the lip's corner.
        className="flex h-full min-w-0 flex-1 select-none items-center gap-2.5 whitespace-nowrap rounded-md pl-3 text-left text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:rounded-tl-assistant"
      >
        <AssistantMark className="h-[18px] w-[18px]" />
        <span className="font-semibold tracking-tight">Steddy Assistant</span>
        {label && <span className="min-w-0 truncate text-muted-foreground">· {label}</span>}
        <span className="ml-auto flex shrink-0 items-center gap-2 pl-2">
          <LipStatus status={status} />
          {!open && unseen > 0 && <Badge className="shadow-none">{unseen} new</Badge>}
        </span>
      </button>
      {children}
      {/* Last on the bar, after the other controls (01a-open); it toggles like the bar itself. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        // A click must not move focus onto this hidden control (the bar keeps it).
        onMouseDown={(event) => event.preventDefault()}
        onClick={onToggle}
        className="flex h-full shrink-0 items-center pl-2"
      >
        <ChevronUp className="h-4 w-4 text-muted-foreground transition-transform duration-travel ease-spring group-data-[state=open]/lip:rotate-180" />
      </button>
      {/* Announced as it changes, while closed; open, the thread says it (one voice, not two). */}
      <span role="status" className="sr-only">
        {!open && status?.text}
      </span>
    </div>
  );
});
